import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { Plus } from "lucide-react";
import ApiDatabase from "server";
import UseContext from "hooks/useContext";
import useModal from "components/modal/useModal";
import Button from "components/button";
import StyledInput from "components/styledInput/StyledInput";
import ProfileChoice from "components/profileChoice/ProfileChoice";
import PrivatePoolIntro from "components/privatePool/PrivatePoolIntro";
import PrivatePoolEmpty from "components/privatePool/PrivatePoolEmpty";
import {
    formatPoolMemberDetail,
    formatPoolMemberName,
    toPrivatePoolProfile,
} from "components/privatePool/privatePoolMember";
import { SearchIcon } from "assets/icons";
import { formatFullName } from "helper/personName";
import useListFillHeight from "pages/createMission/utils/useListFillHeight";
import { INVITED_PROFILE_TYPE, isProfileInvited } from "pages/createMission/utils/invitedProfiles";
import ProfileListSkeleton from "./ProfileListSkeleton";

export const ADD_PRIVATE_POOL_MODAL = "cM_add_private_pool";

const SEARCH_DELAY_MS = 300;

/**
 * "Vivier privé" tab: the company's own candidates, searched by the api. Opening the add modal
 * hands it, through the context's `privatePoolModalData`:
 * - onMemberAdded(member): the member created by the api (201) goes on top of the list, checked;
 * - onTempWorkerInvited({ _id, firstname, lastname }): a temp worker already placed by Jobbiz in
 *   the group is checked as a Jobbiz profile instead.
 */
const PrivatePoolTab = ({ isActive, invitedProfiles, onToggle, onAdd }) => {
    const { data, companyData, language, setPrivatePoolModalData } = UseContext();
    const { toggle } = useModal();
    const label = (key, defaultValue) => language.getValue(`privatePool.${key}`, defaultValue);
    const listRef = useRef(null);
    const requestIdRef = useRef(0);
    const isFirstLoadRef = useRef(true);
    const hasLoadedRef = useRef(false);
    const [search, setSearch] = useState("");
    // The members and the search they answer, so an empty answer tells "empty pool" from "no result".
    const [result, setResult] = useState({ query: "", members: null });
    const [isLoading, setIsLoading] = useState(false);
    // The first load failed: an error with "Réessayer", not the empty pool.
    const [hasLoadError, setHasLoadError] = useState(false);
    const [reloadCount, setReloadCount] = useState(0);

    useEffect(() => {
        if (!companyData?._id) return undefined;
        const query = search.trim();
        const delay = isFirstLoadRef.current ? 0 : SEARCH_DELAY_MS;
        isFirstLoadRef.current = false;
        const timer = setTimeout(() => {
            const requestId = ++requestIdRef.current;
            setIsLoading(true);
            ApiDatabase.getPrivatePool(
                { idCompany: companyData._id, search: query, dateStart: data.dateStart, dateEnd: data.dateEnd },
                (response) => {
                    if (requestId !== requestIdRef.current) return;
                    hasLoadedRef.current = true;
                    setHasLoadError(false);
                    setResult({ query, members: Array.isArray(response?.data) ? response.data : [] });
                    setIsLoading(false);
                },
                () => {
                    if (requestId !== requestIdRef.current) return;
                    setIsLoading(false);
                    if (!hasLoadedRef.current) {
                        setHasLoadError(true);
                        return;
                    }
                    setResult((prev) => ({ query, members: prev.members || [] }));
                    toast.error(label("loadError", "Le vivier privé n'a pas pu être chargé, réessayez plus tard."));
                }
            );
        }, delay);
        return () => clearTimeout(timer);
        // The period cannot change on this step; the search and the company drive the list.
    }, [search, companyData?._id, reloadCount]);

    const retryFirstLoad = () => {
        isFirstLoadRef.current = true;
        setHasLoadError(false);
        setReloadCount((count) => count + 1);
    };

    const handleMemberAdded = useCallback(
        (member) => {
            if (!member?._id) return;
            setResult((prev) => ({
                ...prev,
                members: [member, ...(prev.members || []).filter((item) => item._id !== member._id)],
            }));
            onAdd(toPrivatePoolProfile(member, language));
        },
        [onAdd, language]
    );

    const handleTempWorkerInvited = useCallback(
        (tempWorker) => {
            if (!tempWorker?._id) return;
            onAdd({
                type: INVITED_PROFILE_TYPE.JOBBIZ,
                id: tempWorker._id,
                name: formatFullName(tempWorker.firstname, tempWorker.lastname),
                detail: "",
            });
        },
        [onAdd]
    );

    const openAddModal = () => {
        setPrivatePoolModalData({
            onMemberAdded: handleMemberAdded,
            onTempWorkerInvited: handleTempWorkerInvited,
        });
        toggle(ADD_PRIVATE_POOL_MODAL);
    };

    const members = result.members || [];
    const isPoolEmpty = result.members !== null && !result.query && members.length === 0 && !search.trim();
    const isFirstLoading = result.members === null;

    const listMaxHeight = useListFillHeight(
        listRef,
        isActive,
        `${isLoading}-${members.length}-${isPoolEmpty}-${hasLoadError}`
    );

    return (
        <div className="flex flex-col gap-4">
            <PrivatePoolIntro />
            {hasLoadError ? (
                <div className="flex flex-col items-center gap-3 rounded-lg border px-5 py-8 text-center" role="alert">
                    <p className="ft-sm text-gray-700">
                        {label("firstLoadError", "Le vivier privé n'a pas pu être chargé.")}
                    </p>
                    <Button size="SM" color="SECONDARY" onClick={retryFirstLoad}>
                        {label("retry", "Réessayer")}
                    </Button>
                </div>
            ) : isPoolEmpty ? (
                <PrivatePoolEmpty onAdd={openAddModal} />
            ) : (
                <>
                    <div className="flex items-start gap-3">
                        <div className="min-w-0 flex-1">
                            <StyledInput
                                type={"text"}
                                placeholder={label("searchPlaceholder", "Nom, téléphone ou e-mail")}
                                imgFront={<SearchIcon wh={18} color={"#6B7280"} />}
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </div>
                        <Button size="SM" className="h-10 shrink-0" onClick={openAddModal}>
                            <Plus size={18} aria-hidden="true" />
                            {label("add", "Ajouter")}
                        </Button>
                    </div>
                    <div
                        ref={listRef}
                        className={"flex flex-col overflow-y-auto border rounded-lg"}
                        style={listMaxHeight ? { maxHeight: listMaxHeight } : undefined}
                    >
                        {isFirstLoading || (isLoading && members.length === 0) ? (
                            <ProfileListSkeleton />
                        ) : members.length > 0 ? (
                            members.map((member) => {
                                const name = formatPoolMemberName(member);
                                const detail = formatPoolMemberDetail(member, language);
                                return (
                                    <ProfileChoice
                                        key={member._id}
                                        name={name}
                                        detail={detail}
                                        checked={isProfileInvited(
                                            invitedProfiles,
                                            INVITED_PROFILE_TYPE.PRIVATE_POOL,
                                            member._id
                                        )}
                                        onToggle={() =>
                                            onToggle({
                                                type: INVITED_PROFILE_TYPE.PRIVATE_POOL,
                                                id: member._id,
                                                name,
                                                detail,
                                            })
                                        }
                                    />
                                );
                            })
                        ) : (
                            <p className="px-5 py-8 text-center ft-sm text-gray-500">
                                {label("noResult", "Aucun résultat")}
                            </p>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default PrivatePoolTab;
